import MessageService from "@src/services/message";
import { Toast } from "@src/plugins/toast";
import { useChatSessionStore } from "@src/stores/chatSession";
import ChatSession from "@src/types/ChatSession";
import ChatMessage from "@src/types/ChatMessage";
import { db } from "@src/services/db";
import { useAuthStore } from "@src/stores/auth";

export function useForwardMessages() {
    const sessionStore = useChatSessionStore();
    const authStore = useAuthStore();

    function getOrderedForwardMessages() {
        return [...sessionStore.forwardMessages].sort((a, b) => {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });
    }

    function isForwardableTextType(type: string) {
        return ['text', 'chat', 'ask', 'list', 'warning'].includes(type);
    }

    function isForwardableMediaType(type: string) {
        return ['image', 'audio', 'document'].includes(type);
    }

    async function fetchFileAsBlob(url: string) {
        const response = await fetch(url, { mode: 'cors' });
        return await response.blob();
    }

    function createTempId() {
        return -Date.now() - Math.floor(Math.random() * 10000);
    }

    async function createTempMessageForSession(
        sessionId: number,
        payload: Partial<ChatMessage>
    ) {
        const tempMessage: ChatMessage = {
            id: payload.id!,
            chat_session_id: sessionId,
            user_id: authStore.user?.id ?? null,
            human_date: new Date().toISOString(),
            from: 'user',
            type: payload.type ?? 'text',
            has_media: payload.has_media ?? false,
            is_forwarded: true,
            is_status: false,
            is_starred: false,
            is_edited: false,
            broadcast: false,
            reply_to: undefined,
            selected: false,
            message: payload.message ?? '',
            status: '0',
            sender: {
                id: authStore.user?.id ?? 0,
                name: authStore.user?.name ?? 'Usuário',
                avatar: authStore.user?.profile_photo_path ?? (authStore.user?.profile_photo_url || ''),
                type: 'user'
            },
            reactions: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            progress: payload.progress,
            content: payload.content ?? ''
        };

        await db.messages.put(tempMessage as any);
        await db.sessions.update(sessionId, {
            last_message: tempMessage,
            updated_at: Date.now().toString()
        } as any);

        return tempMessage;
    }

    async function markTempAsFailed(tempId: number) {
        await db.messages.update(tempId as any, {
            status: '-1',
            updated_at: new Date().toISOString(),
            progress: -1
        } as any).catch(() => {});
    }

    async function removeTemp(tempId: number) {
        await db.messages.delete(tempId as any).catch(() => {});
    }

    async function forwardSingleMessageToSession(
        message: ChatMessage,
        targetSession: ChatSession
    ) {
        if (isForwardableTextType(message.type)) {
            const text = message.message ?? '';
            if (!text) return;

            const tempId = createTempId();

            await createTempMessageForSession(targetSession.id, {
                id: tempId,
                type: 'text',
                has_media: false,
                message: text,
            });

            void MessageService
                .sendMessageToSession(targetSession.id, text, tempId, null)
                .then(async () => {
                    await removeTemp(tempId);
                })
                .catch(async () => {
                    await markTempAsFailed(tempId);
                });

            return;
        }

        if (message.type === 'contact') {
            const contactId = Number((message as any).contact_id ?? 0);
            if (!contactId) return;

            void MessageService.sendContactToSession(targetSession.id, contactId, 0);
            return;
        }

        if (message.type === 'location') {
            const locationId = Number((message as any).location_id ?? 0);
            if (!locationId) return;

            void MessageService.sendLocationToSession(targetSession.id, locationId, 0);
            return;
        }

        if (isForwardableMediaType(message.type)) {
            const fileUrl =
                (message as any).content ||
                (message as any).message;

            if (!fileUrl) return;

            const blob = await fetchFileAsBlob(fileUrl);
            const tempId = createTempId();
            const localUrl = URL.createObjectURL(blob);

            await createTempMessageForSession(targetSession.id, {
                id: tempId,
                type: message.type,
                has_media: true,
                message: localUrl,
                progress: 1,
            });

            const extensionByType: Record<string, string> = {
                image: 'jpg',
                audio: 'mp3',
                document: 'pdf',
            };

            const file = new File(
                [blob],
                `forwarded-${Date.now()}.${extensionByType[message.type] || 'bin'}`,
                { type: blob.type || 'application/octet-stream' }
            );

            const formData = new FormData();
            formData.append('file', file);
            formData.append('id', String(targetSession.id));
            formData.append('quoted', 'undefined');
            formData.append('tempId', String(tempId));
            formData.append('type', message.type);
            formData.append('sessionType', 'session');

            void MessageService
                .sendMediaToSession(targetSession.id, formData)
                .then(async () => {
                    await removeTemp(tempId);
                })
                .catch(async () => {
                    await markTempAsFailed(tempId);
                });

            return;
        }

        console.warn('Tipo de mensagem não suportado para encaminhamento:', message.type);
    }

    async function forwardMessagesForSession(
        session: ChatSession,
        orderedMessages: ChatMessage[]
    ) {
        for (const message of orderedMessages) {
            if (!sessionStore.canForwardMessage(message)) continue;
            await forwardSingleMessageToSession(message, session);
        }
    }

    async function handleConfirmForward() {
        if (!sessionStore.forwardTargetSessions.length) return;
        if (!sessionStore.forwardMessages.length) return;

        const orderedMessages = getOrderedForwardMessages() as ChatMessage[];
        const targetSessions = [...sessionStore.forwardTargetSessions].filter(
            session => session.status === 'active' && !session.is_expired
        );

        sessionStore.closeForwardModal();
        sessionStore.stopForwardMode();

        Toast.fire("Encaminhamento iniciado", "", "success");

        void Promise.all(
            targetSessions.map(session =>
                forwardMessagesForSession(session, orderedMessages)
            )
        ).catch((error) => {
            console.error("Erro no encaminhamento em background:", error);
            Toast.fire("Algumas mensagens podem não ter sido encaminhadas", "", "error");
        });
    }

    return {
        handleConfirmForward,
        forwardSingleMessageToSession,
        getOrderedForwardMessages,
    };
}